import { useEffect, useMemo, useState } from 'react'
import {
  BillingService,
  type CatalogueResponse,
  type CheckoutResponse,
  type PlanChangePreview,
  type PlanOption,
} from '../client'
import { useAuth } from '../providers/auth/useAuth'
import { apiErrorCode, apiErrorMessage } from '../lib/api'
import { formatCredits } from '../lib/format'
import posthog from '../lib/posthog'
import { DEMO_URL, PLAN_COPY, YEARLY_DISCOUNT, type Billing, type PlanCopy, intervalFor } from '../lib/plans'
import PlanCard from '../components/billing/PlanCard'
import PlanChangeDialog from '../components/billing/PlanChangeDialog'
import TrimBrandsDialog from '../components/billing/TrimBrandsDialog'

const INCLUDED = ['7-day free trial', 'Cancel anytime', 'No per-seat fees']
const DEFAULT_TOPUP_CREDITS = 5000

export default function Plans() {
  const { account, brand } = useAuth()
  const brandId = brand?.id
  const [billing, setBilling] = useState<Billing>('monthly')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [catalogue, setCatalogue] = useState<CatalogueResponse | null>(null)
  const [pending, setPending] = useState<{ plan: PlanCopy; option: PlanOption } | null>(null)
  const [preview, setPreview] = useState<PlanChangePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [trimming, setTrimming] = useState<{ plan: PlanCopy; option: PlanOption; limit: number } | null>(null)
  const [topupCredits, setTopupCredits] = useState(DEFAULT_TOPUP_CREDITS)

  useEffect(() => {
    let live = true
    BillingService.getPlans()
      .then((response) => live && setCatalogue(response))
      .catch((err) => live && setError(apiErrorMessage(err)))
    return () => {
      live = false
    }
  }, [])

  const interval = intervalFor(billing)
  const optionsByTier = useMemo(() => {
    const map = new Map<string, PlanOption[]>()
    for (const plan of catalogue?.plans ?? []) {
      map.set(
        plan.tier,
        plan.options.filter((option) => option.interval === interval),
      )
    }
    return map
  }, [catalogue, interval])

  async function checkout(type: 'subscription' | 'credit_pack', start: () => Promise<CheckoutResponse>) {
    setBusy(true)
    setError(null)
    try {
      const { url } = await start()
      posthog.capture('checkout_started', { type })
      window.location.assign(url)
    } catch (err) {
      setError(apiErrorCode(err) === 'too_many_brands' ? null : apiErrorMessage(err))
      setBusy(false)
      throw err
    }
  }

  function startCheckout(plan: PlanCopy, option: PlanOption) {
    if (!brandId) return
    checkout('subscription', () =>
      BillingService.subscriptionCheckout({ brand_id: brandId, price_id: option.price_id }),
    ).catch((err: unknown) => {
      if (apiErrorCode(err) === 'too_many_brands') {
        setPending(null)
        setTrimming({ plan, option, limit: brandLimit(plan) })
      }
    })
  }

  function brandLimit(plan: PlanCopy) {
    return catalogue?.plans.find((item) => item.tier === plan.tier)?.brand_limit ?? Infinity
  }

  function selectPlan(plan: PlanCopy, option: PlanOption | null) {
    if (!plan.tier || !option) {
      window.location.assign(DEMO_URL)
      return
    }
    if (!brandId) return

    const limit = brandLimit(plan)
    if ((account?.brands?.length ?? 0) > limit) {
      setTrimming({ plan, option, limit })
      return
    }
    continueWithPlan(plan, option)
  }

  function continueWithPlan(plan: PlanCopy, option: PlanOption) {
    if (!brandId) return

    // Buying a first plan goes through Stripe, which confirms it. Changing one
    // charges without leaving the app, so it is confirmed here instead.
    if (!subscribed) {
      startCheckout(plan, option)
      return
    }

    setPending({ plan, option })
    setPreview(null)
    setPreviewing(true)
    setError(null)
    BillingService.previewPlanChange(brandId, option.price_id)
      .then(setPreview)
      .catch((err: unknown) => setError(apiErrorMessage(err)))
      .finally(() => setPreviewing(false))
  }

  function buyPack(priceId: string, credits?: number) {
    if (!brandId) return
    checkout('credit_pack', () =>
      BillingService.packCheckout({ brand_id: brandId, price_id: priceId, credits }),
    ).catch(() => undefined)
  }

  const packs = catalogue?.packs ?? []
  const topup = catalogue?.topup ?? null
  const topupChosen = topup
    ? Math.min(Math.max(topupCredits, topup.min_credits), topup.max_credits)
    : 0
  const topupFill = topup
    ? `${((topupChosen - topup.min_credits) / (topup.max_credits - topup.min_credits)) * 100}%`
    : '0%'
  const topupRate = topup ? Number(topup.unit_amount) / 100 : 0
  const topupTotal = (topupChosen * topupRate).toLocaleString('en-US', {
    style: 'currency',
    currency: topup?.currency ?? 'USD',
  })
  const subscribed = (account?.subscription ?? 'none') !== 'none'

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8 md:px-6">
      <div className="mx-auto max-w-[1400px]">
        <header className="text-center">
          <h1 className="font-heading text-3xl font-semibold text-stone-900 md:text-4xl">
            Pricing that scales with your team
          </h1>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-sm text-stone-500">
            {INCLUDED.map((item, index) => (
              <span key={item} className="flex items-center gap-3.5">
                {index > 0 && <span className="text-stone-300 max-md:hidden">|</span>}
                {item}
              </span>
            ))}
          </p>

          <div className="mt-7 inline-flex gap-1 rounded-xl bg-stone-200 p-1">
            {(['monthly', 'yearly'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setBilling(option)}
                className={`rounded-lg px-4.5 py-2.5 text-sm font-medium transition-colors ${
                  billing === option
                    ? 'bg-stone-100 text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:bg-black/5 hover:text-stone-700'
                }`}
              >
                {option === 'monthly' ? 'Monthly' : `Yearly (save ${YEARLY_DISCOUNT * 100}%)`}
              </button>
            ))}
          </div>
        </header>

        {error && (
          <p className="mx-auto mt-6 max-w-xl rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="mt-10 grid gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          {PLAN_COPY.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              options={plan.tier ? (optionsByTier.get(plan.tier) ?? []) : []}
              billing={billing}
              current={plan.tier === account?.subscription}
              subscribed={subscribed}
              busy={busy}
              onSelect={(option) => selectPlan(plan, option)}
            />
          ))}
        </div>

        {pending && (
          <PlanChangeDialog
            planName={pending.plan.name}
            preview={preview}
            loading={previewing}
            busy={busy}
            error={error}
            onConfirm={() => startCheckout(pending.plan, pending.option)}
            onCancel={() => {
              setPending(null)
              setError(null)
            }}
          />
        )}

        {trimming && (
          <TrimBrandsDialog
            planName={trimming.plan.name}
            limit={trimming.limit}
            onCancel={() => setTrimming(null)}
            onDone={() => {
              const { plan, option } = trimming
              setTrimming(null)
              continueWithPlan(plan, option)
            }}
          />
        )}

        {(topup || packs.length > 0) && (
          <section className="mt-16">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Top up credits</h2>
            <p className="mt-2 max-w-2xl text-sm text-stone-500">
              One-off credits for a busy month. They stack on top of your plan&rsquo;s monthly allowance and carry over
              for as long as your subscription is active.
            </p>

            <div className="mt-6 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
              {topup && (
                <div className="flex flex-col rounded-2xl border border-stone-200 bg-white p-5 transition-colors hover:border-stone-300">
                  <span className="font-heading text-3xl leading-none font-semibold text-stone-900">
                    {topupChosen.toLocaleString()}
                  </span>
                  <span className="mt-1.5 text-xs font-medium tracking-[0.08em] text-stone-400 uppercase">credits</span>

                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="text-lg font-medium text-stone-900">{topupTotal}</span>
                    <span className="text-xs text-stone-400">${topupRate.toFixed(3)} per credit</span>
                  </div>

                  <input
                    type="range"
                    min={topup.min_credits}
                    max={topup.max_credits}
                    step={topup.step_credits}
                    value={topupChosen}
                    onChange={(event) => setTopupCredits(Number(event.target.value))}
                    aria-label="Top-up credits"
                    className="credit-slider mt-4"
                    style={{ '--fill': topupFill } as React.CSSProperties}
                  />
                  <div className="mb-5 flex justify-between text-xs text-stone-400">
                    <span>{formatCredits(topup.min_credits)}</span>
                    <span>{formatCredits(topup.max_credits)}</span>
                  </div>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => buyPack(topup.price_id, topupChosen)}
                    className="mt-auto inline-flex h-10 items-center justify-center rounded-xl border border-stone-300 bg-white text-sm font-medium text-stone-900 transition-colors enabled:hover:bg-stone-50 disabled:opacity-60"
                  >
                    Buy {formatCredits(topupChosen)} credits
                  </button>
                </div>
              )}

              {packs.map((pack) => (
                <div
                  key={pack.price_id}
                  className="flex flex-col rounded-2xl border border-stone-200 bg-stone-100 p-5 transition-colors hover:border-stone-300"
                >
                  <span className="font-heading text-3xl leading-none font-semibold text-stone-900">
                    {formatCredits(pack.credits)}
                  </span>
                  <span className="mt-1.5 text-xs font-medium tracking-[0.08em] text-stone-400 uppercase">credits</span>

                  <div className="mt-5 mb-5 flex items-baseline gap-2">
                    <span className="text-lg font-medium text-stone-900">${(pack.amount / 100).toLocaleString()}</span>
                    <span className="text-xs text-stone-400">
                      ${(pack.amount / 100 / pack.credits).toFixed(3)} per credit
                    </span>
                  </div>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => buyPack(pack.price_id)}
                    className="mt-auto inline-flex h-10 items-center justify-center rounded-xl border border-stone-300 bg-stone-100 text-sm font-medium text-stone-900 transition-colors enabled:hover:bg-stone-200 disabled:opacity-60"
                  >
                    Buy credits
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
